import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload as UploadIcon, Film, Sparkles, Loader2, Music, Image as ImageIcon, X as XIcon, Link2 as LinkIcon, Crown, ShoppingBag, ExternalLink } from "lucide-react";
import AudioPicker from "@/components/AudioPicker";
import MentionInput from "@/components/MentionInput";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WhatsAppEarningsDialog from "@/components/WhatsAppEarningsDialog";
import { toast } from "sonner";

/**
 * Turn any axios error from an upload into a message the user can act on.
 * Handles: server-typed detail, HTTP status codes, network drops, timeouts,
 * and the very common Cloudflare/proxy 100-MB body-size wall.
 *
 * `stage` — optional label ("init", "chunk 3/12", "complete", "patch-image",
 * "single-shot"). Prepended to the surfaced message so the next report tells
 * us exactly where the upload broke without needing devtools.
 */
function explainUploadError(err, fileBytes = 0, stage = "") {
  const label = stage ? `[${stage}] ` : "";
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return `${label}${detail}`;

  const status = err?.response?.status;
  const mb = Math.round((fileBytes || 0) / (1024 * 1024));

  if (status === 413) {
    return `${label}Upload rejected — file too large (${mb}MB). Server or proxy body-size cap hit.`;
  }
  if (status === 502 || status === 503 || status === 504) {
    return `${label}Upload dropped by the server proxy (${status}). This usually means the file exceeded the platform's body-size limit — try a smaller clip or contact support.`;
  }
  if (status === 401 || status === 403) {
    return `${label}Session expired — please sign in again.`;
  }
  if (err?.code === "ECONNABORTED") {
    return `${label}Upload timed out. Try a smaller file or a stronger connection.`;
  }
  // Classic Axios "network error" — request never got a response. On Cloudflare
  // Free plans anything > 100 MB gets dropped here before the app can respond.
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    if (fileBytes > 100 * 1024 * 1024) {
      return `${label}Upload rejected — the server proxy (Cloudflare) caps requests at 100MB and your file is ${mb}MB. Contact support to raise the ingress limit.`;
    }
    return `${label}Network dropped mid-upload — the request never reached the server. If your internet is fine, try signing out + back in, then retry.`;
  }
  if (status) {
    return `${label}Upload failed (${status}). ${err?.message || "Try again in a moment."}`;
  }
  return `${label}Upload failed — ${err?.message || "no response from server"}.`;
}

export default function Upload() {
  const { user, setUser } = useAuth();
  const [waOpen, setWaOpen] = useState(false);
  const waCheckedRef = useRef(false);

  // Nudge creators who still haven't saved a WhatsApp number to add one — but
  // don't nag every time. We count each upload visit and only pop the dialog
  // on the 1st, 3rd, 6th, 10th, 15th… try (a triangular sequence, so the gap
  // grows over time). Counter is per-account in localStorage; once they add a
  // number (needs=false) we stop counting and never prompt again.
  useEffect(() => {
    if (!user || waCheckedRef.current) return;
    const needs = !(user.whatsapp_number && user.whatsapp_boost_alerts && !user.whatsapp_opted_out);
    if (!needs) return;
    waCheckedRef.current = true;
    const key = `wa_upload_tries_${user.user_id}`;
    let tries = 0;
    try { tries = parseInt(localStorage.getItem(key) || "0", 10) || 0; } catch { /* ignore */ }
    tries += 1;
    try { localStorage.setItem(key, String(tries)); } catch { /* ignore */ }
    // A number n is triangular (1,3,6,10,15,…) iff 8n+1 is a perfect square.
    const root = Math.sqrt(8 * tries + 1);
    if (Number.isInteger(root)) setWaOpen(true);
  }, [user]);

  const closeWa = () => setWaOpen(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const [mode, setMode] = useState(searchParams.get("mode") === "photo" || searchParams.get("track") ? "photo" : "video");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]); // multi-slide: [{file, preview}]
  const [audioFile, setAudioFile] = useState(null);
  const [audioLabel, setAudioLabel] = useState("");
  const [musicTrackId, setMusicTrackId] = useState("");
  const [caption, setCaption] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);      // 0-100
  const [uploadStage, setUploadStage] = useState("");           // "uploading" | "processing" | ""
  const [embedUrl, setEmbedUrl] = useState("");

  // Product link — creators can attach a guyanadiscounts.com URL that
  // replaces the Share button on the feed with a small product thumbnail.
  const [productUrl, setProductUrl] = useState("");
  const [productPreview, setProductPreview] = useState(null); // { image_url, title, description }
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);
  const productFetchTimerRef = useRef(null);
  // Optional custom thumbnail — if set, uploaded alongside the video and
  // used as the SHOP button image instead of the auto-scraped OG image.
  const [productImage, setProductImage] = useState(null);      // File
  const [productImagePreview, setProductImagePreview] = useState(null); // dataURL
  const productImageRef = useRef(null);
  // Debounced preview state for the link tab — populated ~500ms after the
  // last keystroke, cleared while a new fetch is in-flight.
  const [embedPreview, setEmbedPreview] = useState(null);
  const [embedPreviewLoading, setEmbedPreviewLoading] = useState(false);
  const [embedPreviewError, setEmbedPreviewError] = useState(null);
  const embedFetchTimerRef = useRef(null);
  const [limits, setLimits] = useState({ max_bytes: 100 * 1024 * 1024, max_duration_sec: 60, is_pro: false });

  // Fetch upload limits (Pro-aware) on mount and whenever user changes.
  useEffect(() => {
    api.get("/upload/limits").then((r) => setLimits(r.data)).catch(() => {});
  }, [user]);

  // Debounced product preview — fires ~700ms after the last keystroke.
  useEffect(() => {
    if (productFetchTimerRef.current) clearTimeout(productFetchTimerRef.current);
    const trimmed = (productUrl || "").trim();
    if (!trimmed) {
      setProductPreview(null);
      setProductError(null);
      setProductLoading(false);
      return;
    }
    // Basic client-side gate — must be from a supported shop domain.
    if (!/^https?:\/\/(www\.)?(guyanadiscounts\.com|aiwebmatrix\.online|play\.matrixshopping\.com)\//i.test(trimmed)) {
      setProductPreview(null);
      setProductError("Only guyanadiscounts.com and aiwebmatrix.online links are supported right now.");
      setProductLoading(false);
      return;
    }
    setProductError(null);
    setProductLoading(true);
    productFetchTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/upload/product-preview?url=${encodeURIComponent(trimmed)}`);
        setProductPreview(r.data);
        setProductError(null);
      } catch (e) {
        setProductPreview(null);
        setProductError(e?.response?.data?.detail || "Couldn't load product preview");
      } finally {
        setProductLoading(false);
      }
    }, 700);
    return () => {
      if (productFetchTimerRef.current) clearTimeout(productFetchTimerRef.current);
    };
  }, [productUrl]);

  // If landed with ?track=<id> (from Discover's Trending Sounds), pre-attach.
  useEffect(() => {
    const trackId = searchParams.get("track");
    if (!trackId || audioFile) return;
    let cancelled = false;
    (async () => {
      try {
        const meta = await api.get(`/music/library/${trackId}`);
        const t = meta.data;
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const url = t.url?.startsWith("http") ? t.url : `${backend}${t.url}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        if (cancelled) return;
        // Pick a file extension that matches the actual blob mime so the
        // Content-Disposition + backend's `audio/*` gate both see a real
        // audio file. Original-sound tracks come back as `audio/mp4`
        // (server-extracted m4a); curated tracks are `audio/mpeg` mp3s.
        const isM4a = (blob.type || "").includes("audio/mp4");
        const ext = isM4a ? "m4a" : "mp3";
        const fallbackType = isM4a ? "audio/mp4" : "audio/mpeg";
        const filename = `${t.title.replace(/[^a-z0-9]+/gi, "_")}.${ext}`;
        const f = new File([blob], filename, { type: blob.type || fallbackType });
        setAudioFile(f);
        setAudioLabel(`${t.title} · ${t.artist}`);
        setMusicTrackId(t.id);
        toast.success(`Pre-loaded: ${t.title}`);
      } catch {
        toast.error("Couldn't preload that sound — pick another from the library");
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, audioFile]);

  // Debounced preview lookup for the "link" tab. Fires ~500ms after the
  // user stops typing so we don't hammer the endpoint on every keystroke.
  useEffect(() => {
    clearTimeout(embedFetchTimerRef.current);
    if (mode !== "link") return;
    const trimmed = embedUrl.trim();
    if (!trimmed) {
      setEmbedPreview(null);
      setEmbedPreviewError(null);
      setEmbedPreviewLoading(false);
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setEmbedPreview(null);
      setEmbedPreviewError(null);
      setEmbedPreviewLoading(false);
      return;
    }
    embedFetchTimerRef.current = setTimeout(async () => {
      setEmbedPreviewLoading(true);
      setEmbedPreviewError(null);
      try {
        const r = await api.get("/videos/embed/preview", {
          params: { url: trimmed },
        });
        setEmbedPreview(r.data);
      } catch (err) {
        setEmbedPreview(null);
        setEmbedPreviewError(
          err?.response?.data?.detail || "Couldn't resolve that link",
        );
      } finally {
        setEmbedPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(embedFetchTimerRef.current);
  }, [embedUrl, mode]);

  if (!user) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF007F] mb-3">
            // auth.required
          </p>
          <h2 className="font-display font-black text-3xl mb-4">
            sign in to upload
          </h2>
          <p className="text-white/60 mb-8">
            You need to be logged in to broadcast your transmission.
          </p>
          <button
            data-testid="upload-go-login-btn"
            onClick={() => navigate("/login")}
            className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) {
      toast.error("Please choose a video file");
      return;
    }
    if (f.size > limits.max_bytes) {
      const mb = Math.round(limits.max_bytes / (1024 * 1024));
      toast.error(
        limits.is_pro
          ? `Max size is ${mb}MB`
          : `Max size is ${mb}MB — upgrade to MatrixShopping Pro for 500MB uploads`
      );
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const pickProductImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    setProductImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProductImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearProductImage = () => {
    setProductImage(null);
    setProductImagePreview(null);
    if (productImageRef.current) productImageRef.current.value = "";
  };


  const generateCaption = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Describe your video first");
      return;
    }
    setAiLoading(true);
    try {
      const r = await api.post("/ai/caption", { prompt: aiPrompt });
      setCaption(r.data.caption);
      toast.success("AI caption generated");
    } catch {
      toast.error("AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "video") {
      if (!file) {
        toast.error("Choose a video first");
        return;
      }
      setUploading(true);
      setUploadProgress(0);
      setUploadStage("uploading");
      try {
        // Chunked upload for larger files — a single-shot POST for a
        // 60MB+ video on mobile can easily exceed Cloudflare's ~100s
        // edge timeout and returns a 524 to the user. Chunking each
        // 4MB slice keeps every individual request comfortably inside
        // that window regardless of upload speed.
        const CHUNK_THRESHOLD = 20 * 1024 * 1024; // 20MB
        const useChunked = file.size > CHUNK_THRESHOLD;
        console.info("[upload] start", {
          filename: file.name,
          mime: file.type,
          size_mb: Math.round(file.size / 1024 / 1024),
          use_chunked: useChunked,
        });
        // Stage tracker so the catch block knows exactly where we blew up.
        let stage = useChunked ? "init" : "single-shot";
        if (useChunked) {
          let initResp;
          try {
            initResp = await api.post(
              "/videos/upload/init",
              new URLSearchParams({
                total_bytes: String(file.size),
                filename: file.name || "video.mp4",
                content_type: file.type || "video/mp4",
              }),
              { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
            );
          } catch (e) {
            // Re-throw with stage-labelled diagnostic so the toast tells
            // the user (and us) exactly what failed.
            e.__stage = "init";
            throw e;
          }
          const { upload_id, chunk_size } = initResp.data;
          if (!upload_id) {
            throw new Error("Upload init returned no upload_id — try again");
          }
          const chunkSize = chunk_size || 4 * 1024 * 1024;
          const totalChunks = Math.ceil(file.size / chunkSize);
          for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const slice = file.slice(start, end);
            // Safari sometimes returns 0-byte slices after the picker has
            // been backgrounded or the file reference lost. Bail loud
            // instead of silently uploading empties (which would /complete
            // 400 with "Missing chunks" and confuse the user).
            if (!slice || slice.size === 0) {
              throw new Error(`Chunk ${i} slice returned 0 bytes — reselect the video and retry.`);
            }
            const fd = new FormData();
            fd.append("chunk", slice, "chunk.bin");
            fd.append("chunk_index", String(i));
            fd.append("total_chunks", String(totalChunks));
            // Retry each chunk up to 3 times on transient failures.
            let lastErr = null;
            let sent = false;
            stage = `chunk ${i + 1}/${totalChunks}`;
            for (let attempt = 1; attempt <= 3 && !sent; attempt++) {
              try {
                // IMPORTANT: do NOT set Content-Type manually — axios auto-
                // generates `multipart/form-data; boundary=…` from the
                // FormData. Setting it manually strips the boundary and
                // upstream proxies (Cloudflare) drop the body silently.
                const chunkResp = await api.post(
                  `/videos/upload/${upload_id}/chunk`, fd,
                  { timeout: 90 * 1000 },
                );
                if (!chunkResp?.data || chunkResp.data.size !== slice.size) {
                  throw new Error(
                    `Chunk ${i} size mismatch (sent ${slice.size}, server got ${chunkResp?.data?.size})`,
                  );
                }
                sent = true;
              } catch (e) {
                lastErr = e;
                if (attempt < 3) {
                  await new Promise((r) => setTimeout(r, 800 * attempt));
                }
              }
            }
            if (!sent) {
              if (lastErr) lastErr.__stage = stage;
              throw lastErr;
            }
            // Progress is chunks-based; add ~1% while the final
            // /complete call runs so we don't sit at 100% for a beat.
            const pct = Math.min(99, Math.round(((i + 1) / totalChunks) * 99));
            setUploadProgress(pct);
          }
          setUploadStage("processing");
          setUploadProgress(99);
          const completeFd = new URLSearchParams({
            total_chunks: String(totalChunks),
            caption,
          });
          if (productPreview && productUrl) {
            completeFd.append("product_url", productUrl.trim());
          }
          stage = "complete";
          let completeResp;
          try {
            completeResp = await api.post(`/videos/upload/${upload_id}/complete`, completeFd, {
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              timeout: 90 * 1000,
            });
          } catch (e) {
            e.__stage = "complete";
            throw e;
          }
          // If a custom product image was picked, PATCH it in after the
          // video doc exists — URL-encoded /complete can't carry files.
          if (productImage && productUrl && completeResp?.data?.id) {
            try {
              const pfd = new FormData();
              pfd.append("image", productImage);
              await api.patch(`/videos/${completeResp.data.id}/product-image`, pfd, {
                timeout: 60 * 1000,
              });
            } catch (e) {
              toast.error("Custom shop image failed to attach — using site's default");
            }
          }
          setUploadProgress(100);
        } else {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("caption", caption);
          if (productPreview && productUrl) {
            fd.append("product_url", productUrl.trim());
          }
          if (productImage && productUrl) {
            fd.append("product_image", productImage);
          }
          // Do NOT set Content-Type manually — axios generates the correct
          // multipart/form-data + boundary from the FormData object.
          try {
            await api.post("/videos/upload", fd, {
              // 15 min ceiling — big files on slow uplinks need it
              timeout: 15 * 60 * 1000,
              onUploadProgress: (evt) => {
                if (!evt.total) return;
                const pct = Math.round((evt.loaded / evt.total) * 100);
                setUploadProgress(pct);
                if (pct >= 100) setUploadStage("processing");
              },
            });
          } catch (e) {
            e.__stage = "single-shot";
            throw e;
          }
        }
        toast.success("Uploaded — entering the feed");
        navigate("/");
      } catch (err) {
        console.error("[upload] failed", {
          stage: err?.__stage || "?",
          code: err?.code,
          status: err?.response?.status,
          message: err?.message,
          detail: err?.response?.data?.detail,
        });
        toast.error(explainUploadError(err, file.size, err?.__stage));
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setUploadStage("");
      }
      return;
    }
    if (mode === "link") {
      const url = embedUrl.trim();
      if (!url) {
        toast.error("Paste a video link first");
        return;
      }
      setUploading(true);
      try {
        const body = { url, caption };
        if (productPreview && productUrl) body.product_url = productUrl.trim();
        const r = await api.post("/videos/embed", body);
        // If a custom product image was picked, PATCH it in AFTER the
        // video doc exists — the /videos/embed endpoint takes JSON, not files.
        if (productImage && productUrl && r?.data?.id) {
          try {
            const pfd = new FormData();
            pfd.append("image", productImage);
            await api.patch(`/videos/${r.data.id}/product-image`, pfd, { timeout: 60 * 1000 });
          } catch {
            toast.error("Custom shop image failed to attach — using site's default");
          }
        }
        toast.success("Link posted — entering the feed");
        navigate("/");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't post that link");
      } finally {
        setUploading(false);
      }
      return;
    }
    // Photo + Music mode
    if (!photoFiles.length) {
      toast.error("Choose at least one photo");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadStage("uploading");
    const fd = new FormData();
    photoFiles.forEach(({ file }) => fd.append("photos", file));
    if (audioFile) fd.append("audio", audioFile);
    if (musicTrackId) fd.append("music_track_id", musicTrackId);
    fd.append("caption", caption);
    // Photo posts get the same shop-link treatment as videos — the URL is
    // resolved server-side into OG image + title so the "SHOP" overlay
    // surfaces on the swipeable carousel.
    if (productPreview && productUrl) {
      fd.append("product_url", productUrl.trim());
    }
    try {
      const photoResp = await api.post("/photos/upload", fd, {
        // Do NOT set Content-Type manually — axios generates
        // multipart/form-data + boundary from the FormData.
        timeout: 15 * 60 * 1000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(pct);
          if (pct >= 100) setUploadStage("processing");
        },
      });
      // Custom product image (optional) — attached AFTER upload succeeds via
      // the existing /videos/{id}/product-image PATCH endpoint. Non-fatal
      // if it fails; the OG image the backend already resolved is a safe
      // fallback.
      if (productImage && productUrl && photoResp?.data?.id) {
        try {
          const pfd = new FormData();
          pfd.append("image", productImage);
          await api.patch(`/videos/${photoResp.data.id}/product-image`, pfd, { timeout: 60 * 1000 });
        } catch {
          toast.error("Custom shop image failed to attach — using site's default");
        }
      }
      const slides = photoFiles.length;
      toast.success(
        audioFile
          ? `${slides} slide${slides > 1 ? "s" : ""} + track posted`
          : `${slides} slide${slides > 1 ? "s" : ""} posted`
      );
      navigate("/");
    } catch (err) {
      const totalBytes = photoFiles.reduce((sum, p) => sum + (p.file?.size || 0), 0) + (audioFile?.size || 0);
      toast.error(explainUploadError(err, totalBytes));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStage("");
    }
  };

  const onPickPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 10 - photoFiles.length;
    if (remaining <= 0) {
      toast.error("Max 10 photos per post");
      return;
    }
    const toAdd = files.slice(0, remaining);
    const rejected = [];
    const accepted = [];
    for (const f of toAdd) {
      if (!f.type.startsWith("image/")) {
        rejected.push(`${f.name}: not an image`);
        continue;
      }
      if (f.size > 15 * 1024 * 1024) {
        rejected.push(`${f.name}: too large`);
        continue;
      }
      accepted.push({ file: f, preview: URL.createObjectURL(f), id: `${Date.now()}_${Math.random()}` });
    }
    if (rejected.length) toast.error(rejected[0]);
    setPhotoFiles((prev) => [...prev, ...accepted]);
    // reset input so same file can be re-picked later
    if (photoRef.current) photoRef.current.value = "";
  };

  const removePhoto = (id) => {
    setPhotoFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <WhatsAppEarningsDialog open={waOpen} onClose={closeWa} user={user} onSaved={setUser} />
      <div className="max-w-2xl mx-auto pt-10 px-6">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#39FF14] mb-2">
          // new.transmission
        </p>
        <h1 className="font-display font-black text-4xl mb-2">Upload</h1>
        <p className="text-white/50 text-sm mb-4">
          Vertical, looping, hypnotic. Max {Math.round(limits.max_bytes / (1024 * 1024))}MB · any length.
        </p>
        {!limits.is_pro && (
          <button
            type="button"
            data-testid="upload-pro-upsell-btn"
            onClick={() => navigate("/pro")}
            className="w-full mb-6 flex items-center justify-between gap-3 border border-[#B026FF]/30 hover:border-[#B026FF]/70 bg-[#B026FF]/5 hover:bg-[#B026FF]/10 px-4 py-2.5 transition-all group"
          >
            <div className="flex items-center gap-2 text-left">
              <Crown className="h-4 w-4 text-[#B026FF]" strokeWidth={2.2} />
              <span className="font-mono text-[11px] uppercase tracking-widest text-white/70 group-hover:text-white">
                Pro · 500MB uploads
              </span>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#B026FF]">
              upgrade →
            </span>
          </button>
        )}

        <button
          type="button"
          data-testid="upload-go-generate-btn"
          onClick={() => navigate("/generate")}
          className="w-full border border-[#FF007F]/40 hover:border-[#FF007F] bg-[#FF007F]/5 hover:bg-[#FF007F]/10 p-4 mb-10 flex items-center justify-between gap-4 transition-all group"
        >
          <div className="text-left">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-1">
              // new · sora.engine
            </p>
            <p className="font-display font-bold text-lg text-white">
              Don&apos;t have a video? Generate one with AI →
            </p>
          </div>
          <div className="text-[#FF007F] font-mono text-xs uppercase tracking-widest whitespace-nowrap group-hover:translate-x-1 transition-transform">
            try ai studio
          </div>
        </button>

        <form onSubmit={submit} className="space-y-8">
          {/* Mode tabs: Video vs Photo + Music */}
          <div
            data-testid="upload-mode-tabs"
            className="grid grid-cols-3 gap-2 border border-white/10 p-1"
          >
            <button
              type="button"
              data-testid="upload-mode-video"
              onClick={() => setMode("video")}
              className={`py-2.5 flex items-center justify-center gap-2 transition-all ${
                mode === "video"
                  ? "bg-[#39FF14]/15 text-[#39FF14] shadow-[0_0_12px_rgba(57,255,20,0.25)]"
                  : "text-white/50 hover:text-white"
              }`}
            >
              <Film className="h-4 w-4" strokeWidth={1.8} />
              <span className="font-mono text-xs uppercase tracking-widest">video</span>
            </button>
            <button
              type="button"
              data-testid="upload-mode-link"
              onClick={() => setMode("link")}
              className={`py-2.5 flex items-center justify-center gap-2 transition-all ${
                mode === "link"
                  ? "bg-[#00FFFF]/15 text-[#00FFFF] shadow-[0_0_12px_rgba(0,255,255,0.25)]"
                  : "text-white/50 hover:text-white"
              }`}
            >
              <LinkIcon className="h-4 w-4" strokeWidth={1.8} />
              <span className="font-mono text-xs uppercase tracking-widest">link</span>
            </button>
            <button
              type="button"
              data-testid="upload-mode-photo"
              onClick={() => setMode("photo")}
              className={`py-2.5 flex items-center justify-center gap-2 transition-all ${
                mode === "photo"
                  ? "bg-[#FF007F]/15 text-[#FF007F] shadow-[0_0_12px_rgba(255,0,127,0.25)]"
                  : "text-white/50 hover:text-white"
              }`}
            >
              <ImageIcon className="h-4 w-4" strokeWidth={1.8} />
              <span className="font-mono text-xs uppercase tracking-widest">photo + music</span>
            </button>
          </div>

          {/* Link mode — paste a social URL and we embed / preview it. */}
          {mode === "link" && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
                paste video link
              </label>
              <input
                type="url"
                inputMode="url"
                data-testid="upload-embed-url-input"
                placeholder="https://youtube.com/watch?v=... or tiktok.com/@user/video/..."
                value={embedUrl}
                onChange={(e) => setEmbedUrl(e.target.value)}
                className="w-full bg-white/5 border border-white/10 focus:border-[#00FFFF] focus:ring-1 focus:ring-[#00FFFF]/40 outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-white/30"
              />
              <p className="font-mono text-[10px] text-white/40 mt-2 leading-snug">
                Works with YouTube, Vimeo, TikTok, Instagram, Twitter/X, Reddit, Twitch, Dailymotion, and most oEmbed platforms.
                YouTube and Vimeo play inline in the feed; others open in a new tab from a preview card.
              </p>

              {/* Live preview — thumbnail + title + provider chip. */}
              {embedPreviewLoading && (
                <div
                  data-testid="upload-embed-preview-loading"
                  className="mt-3 flex items-center gap-2 border border-white/10 px-3 py-3 font-mono text-[11px] uppercase tracking-widest text-white/40"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  resolving link…
                </div>
              )}
              {!embedPreviewLoading && embedPreviewError && (
                <div
                  data-testid="upload-embed-preview-error"
                  className="mt-3 border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-[#FF3B30]"
                >
                  {embedPreviewError}
                </div>
              )}
              {!embedPreviewLoading && !embedPreviewError && embedPreview && (
                <div
                  data-testid="upload-embed-preview-card"
                  className="mt-3 flex gap-3 border border-[#00FFFF]/30 bg-[#00FFFF]/5 p-3"
                >
                  <div className="shrink-0 h-20 w-32 bg-black border border-white/10 overflow-hidden flex items-center justify-center">
                    {embedPreview.thumbnail_url ? (
                      <img
                        src={embedPreview.thumbnail_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <LinkIcon className="h-6 w-6 text-white/30" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FFFF] mb-1">
                      // {embedPreview.source}
                      {embedPreview.is_iframeable
                        ? " · inline embed"
                        : " · link card"}
                    </p>
                    <p
                      className="font-display font-bold text-sm text-white line-clamp-2"
                      data-testid="upload-embed-preview-title"
                    >
                      {embedPreview.title || embedPreview.original_url}
                    </p>
                    {embedPreview.author_name && (
                      <p className="font-mono text-[10px] text-white/50 mt-1 truncate">
                        by {embedPreview.author_name}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Duplicate warning — shows underneath the preview when another
                  creator has already posted the same source. Non-blocking:
                  we still let the user submit if they want to add commentary. */}
              {!embedPreviewLoading && embedPreview?.duplicate && (
                <div
                  data-testid="upload-embed-duplicate-warning"
                  className="mt-3 border border-[#FFB800]/40 bg-[#FFB800]/5 px-3 py-2 font-mono text-[11px] text-[#FFB800] leading-snug"
                >
                  <span className="uppercase tracking-widest text-[10px] block mb-0.5">
                    // already.on.feed
                  </span>
                  Posted by{" "}
                  <span className="font-bold">
                    @{embedPreview.duplicate.username || embedPreview.duplicate.user_id}
                  </span>
                  {embedPreview.duplicate.created_at && (
                    <>
                      {" "}on{" "}
                      {new Date(embedPreview.duplicate.created_at).toLocaleDateString(
                        [],
                        { month: "short", day: "numeric", year: "2-digit" },
                      )}
                    </>
                  )}
                  . You can still post it if you want to add commentary.
                </div>
              )}
            </div>
          )}

          {mode === "photo" && (
            <>
              {/* Photo picker — supports multiple slides */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block flex items-center gap-2">
                  photos
                  <span className="text-white/30 normal-case tracking-normal font-sans">
                    — 1–10 slides · swipeable carousel in the feed
                  </span>
                  {photoFiles.length > 0 && (
                    <span
                      data-testid="photo-slide-count"
                      className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#FF007F]"
                    >
                      {photoFiles.length}/10
                    </span>
                  )}
                </label>
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="upload-photo-input"
                  onChange={onPickPhoto}
                />

                {photoFiles.length > 0 ? (
                  <div
                    data-testid="upload-photo-grid"
                    className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2"
                  >
                    {photoFiles.map((p, idx) => (
                      <div
                        key={p.id}
                        data-testid={`upload-slide-${idx}`}
                        className="relative aspect-[3/4] group border border-white/10"
                      >
                        <img
                          src={p.preview}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute top-1 left-1 h-5 w-5 flex items-center justify-center bg-black/70 border border-[#FF007F]/40 text-[#FF007F] font-display font-black text-[10px]">
                          {idx + 1}
                        </div>
                        <button
                          type="button"
                          data-testid={`upload-slide-remove-${idx}`}
                          onClick={() => removePhoto(p.id)}
                          className="absolute top-1 right-1 h-5 w-5 bg-black/80 border border-white/30 hover:border-[#FF007F] hover:text-[#FF007F] text-white flex items-center justify-center opacity-70 hover:opacity-100"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {photoFiles.length < 10 && (
                      <button
                        type="button"
                        data-testid="upload-photo-add-more"
                        onClick={() => photoRef.current?.click()}
                        className="aspect-[3/4] border-2 border-dashed border-white/20 hover:border-[#FF007F]/60 hover:bg-[#FF007F]/5 flex flex-col items-center justify-center gap-1 transition-all group"
                      >
                        <ImageIcon className="h-6 w-6 text-white/40 group-hover:text-[#FF007F]" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 group-hover:text-[#FF007F]">
                          add.more
                        </span>
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="upload-photo-dropzone"
                    onClick={() => photoRef.current?.click()}
                    className="w-full border-2 border-dashed border-white/20 hover:border-[#FF007F]/60 hover:bg-[#FF007F]/5 p-10 flex flex-col items-center justify-center transition-all min-h-[260px] relative overflow-hidden group"
                  >
                    <ImageIcon className="h-10 w-10 text-white/40 group-hover:text-[#FF007F] mb-4 transition-colors" strokeWidth={1.5} />
                    <p className="text-white font-semibold mb-1">
                      Click to choose photos
                    </p>
                    <p className="font-mono text-xs text-white/40 uppercase tracking-widest">
                      1–10 slides · jpg · png · webp · max 15 mb each
                    </p>
                  </button>
                )}
              </div>

              {/* Audio picker — upload / extract-from-video / library tabs */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block flex items-center gap-2">
                  music track
                  <span className="text-white/30 normal-case tracking-normal font-sans">
                    — optional. Loops behind your photo in the feed.
                  </span>
                </label>
                {audioFile ? (
                  <div
                    data-testid="upload-audio-picked"
                    className="flex items-center gap-3 border border-[#00FFFF]/40 bg-[#00FFFF]/5 px-4 py-3"
                    style={{ boxShadow: "0 0 14px rgba(0,255,255,0.2)" }}
                  >
                    <Music
                      className="h-5 w-5 text-[#00FFFF] flex-shrink-0"
                      strokeWidth={1.8}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">
                        {audioLabel || audioFile.name}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        {(audioFile.size / (1024 * 1024)).toFixed(1)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="upload-audio-clear"
                      onClick={() => {
                        setAudioFile(null);
                        setAudioLabel("");
                        setMusicTrackId("");
                      }}
                      className="h-7 w-7 border border-white/20 hover:border-[#FF007F] hover:text-[#FF007F] text-white/60 flex items-center justify-center transition-colors"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <AudioPicker
                    onPick={(f, label, meta) => {
                      setAudioFile(f);
                      setAudioLabel(label || (f?.name ?? ""));
                      setMusicTrackId(meta?.trackId || "");
                    }}
                  />
                )}
              </div>
            </>
          )}

          {mode === "video" && (
          <>
          {/* dropzone */}
          <button
            type="button"
            data-testid="upload-dropzone"
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-white/20 hover:border-[#39FF14]/60 hover:bg-[#39FF14]/5 p-10 flex flex-col items-center justify-center transition-all min-h-[260px] relative overflow-hidden group"
          >
            {previewUrl ? (
              <>
                <video
                  src={previewUrl}
                  className="absolute inset-0 w-full h-full object-cover opacity-50"
                  muted
                  autoPlay
                  loop
                  playsInline
                />
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative z-10 text-center">
                  <Film className="h-10 w-10 mx-auto text-[#39FF14] mb-3" strokeWidth={1.5} />
                  <p className="text-white font-semibold">{file?.name}</p>
                  <p className="font-mono text-xs text-white/60 mt-1">
                    {(file?.size / (1024 * 1024)).toFixed(1)} MB · click to change
                  </p>
                </div>
              </>
            ) : (
              <>
                <UploadIcon className="h-10 w-10 text-white/40 group-hover:text-[#39FF14] mb-4 transition-colors" strokeWidth={1.5} />
                <p className="text-white font-semibold mb-1">
                  Click to choose a video
                </p>
                <p className="font-mono text-xs text-white/40 uppercase tracking-widest">
                  mp4 · mov · webm · max 100mb
                </p>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={onPick}
              data-testid="upload-file-input"
            />
          </button>
          </>
          )}

          {/* AI caption */}
          <div className="bg-[#0A0A0C] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FFFF]">
                  // ai.copywriter
                </p>
                <h3 className="font-display font-bold text-lg mt-1">
                  Spin up an AI caption
                </h3>
              </div>
              <Sparkles className="h-5 w-5 text-[#00FFFF]" />
            </div>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              data-testid="ai-prompt-input"
              placeholder="A cyberpunk dancer in a neon alley at midnight…"
              rows={2}
              className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#00FFFF] focus:ring-1 focus:ring-[#00FFFF]/40 font-mono resize-none"
            />
            <button
              type="button"
              onClick={generateCaption}
              disabled={aiLoading}
              data-testid="generate-caption-btn"
              className="mt-3 w-full sm:w-auto bg-transparent border border-[#00FFFF]/50 text-[#00FFFF] hover:bg-[#00FFFF]/10 px-5 py-2.5 font-mono text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {aiLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  generating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  generate caption
                </>
              )}
            </button>
          </div>

          {/* Product Link — attach a guyanadiscounts.com product URL.
              On the feed, the Share button gets swapped for a small
              product thumbnail box linking to this URL. */}
          <div data-testid="product-link-block" className="bg-[#0A0A0C] border border-[#FF9500]/25 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF9500]">
                  // product.link
                </p>
                <h3 className="font-display font-bold text-lg mt-1">
                  Attach a shop item
                </h3>
                <p className="text-[11px] text-white/50 mt-0.5">
                  Paste a <span className="font-mono text-[#FF9500]">guyanadiscounts.com</span> or <span className="font-mono text-[#FF9500]">aiwebmatrix.online</span> URL. Replaces the Share button on the feed with the linked page&apos;s thumbnail.{" "}
                  <a
                    href="/shop-analytics"
                    data-testid="link-shop-analytics"
                    className="underline decoration-dotted text-[#FF9500]/80 hover:text-[#FF9500]"
                  >
                    See your click stats →
                  </a>
                </p>
              </div>
              <ShoppingBag className="h-5 w-5 text-[#FF9500]" />
            </div>
            <input
              type="url"
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              data-testid="product-url-input"
              placeholder="https://guyanadiscounts.com/product/…  or  https://aiwebmatrix.online/…"
              className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF9500] focus:ring-1 focus:ring-[#FF9500]/40 font-mono"
            />

            {/* Optional custom shop-button image. If set, overrides the OG
                thumbnail that would otherwise get scraped from the URL. */}
            {productUrl && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                    custom shop image (optional)
                  </p>
                  {productImage && (
                    <button
                      type="button"
                      data-testid="product-image-clear"
                      onClick={clearProductImage}
                      className="text-[10px] font-mono text-white/40 hover:text-[#FF3B30]"
                    >
                      remove
                    </button>
                  )}
                </div>
                <input
                  ref={productImageRef}
                  type="file"
                  accept="image/*"
                  data-testid="product-image-input"
                  className="hidden"
                  onChange={(e) => pickProductImage(e.target.files?.[0])}
                />
                {productImagePreview ? (
                  <button
                    type="button"
                    data-testid="product-image-swap"
                    onClick={() => productImageRef.current?.click()}
                    className="flex items-center gap-3 border border-[#FF9500]/60 bg-[#FF9500]/[0.05] p-2 w-full hover:border-[#FF9500]"
                  >
                    <div className="w-14 h-14 shrink-0 bg-black/50 overflow-hidden border border-white/10">
                      <img src={productImagePreview} alt="custom" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <p className="text-xs font-bold text-white truncate">Custom image ready</p>
                      <p className="text-[10px] font-mono text-[#FF9500]/80">Overrides site thumbnail · click to swap</p>
                    </div>
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="product-image-pick"
                    onClick={() => productImageRef.current?.click()}
                    className="w-full border border-dashed border-white/20 hover:border-[#FF9500] py-4 flex flex-col items-center gap-1 text-white/50 hover:text-[#FF9500] transition-all"
                  >
                    <ImageIcon className="h-5 w-5" strokeWidth={1.5} />
                    <span className="text-[10px] font-mono uppercase tracking-wider">upload custom image</span>
                    <span className="text-[9px] text-white/30">leave blank to use site&apos;s thumbnail</span>
                  </button>
                )}
              </div>
            )}
            {productLoading && (
              <p className="mt-2 font-mono text-[10px] text-white/40 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                loading preview…
              </p>
            )}
            {productError && !productLoading && (
              <p data-testid="product-link-error" className="mt-2 font-mono text-[10px] text-[#FF3B30]">
                {productError}
              </p>
            )}
            {productPreview && !productLoading && (
              <div
                data-testid="product-link-preview"
                className="mt-3 flex items-center gap-3 border border-[#FF9500]/40 bg-[#FF9500]/[0.05] p-2"
              >
                <div className="w-16 h-16 shrink-0 bg-black/50 overflow-hidden border border-white/10">
                  <img
                    src={productPreview.image_url}
                    alt={productPreview.title || "product"}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-white truncate">
                    {productPreview.title || "Untitled product"}
                  </p>
                  <p className="text-[10px] font-mono text-[#FF9500] flex items-center gap-1 mt-0.5">
                    <ExternalLink className="h-3 w-3" />
                    {new URL(productPreview.url).hostname}
                  </p>
                  {productPreview.description && (
                    <p className="text-[10px] text-white/40 truncate mt-0.5">
                      {productPreview.description}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="product-link-clear"
                  onClick={() => { setProductUrl(""); setProductPreview(null); }}
                  className="shrink-0 p-1 text-white/40 hover:text-[#FF3B30]"
                  title="Remove product link"
                >
                  <XIcon className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            )}
          </div>

          {/* Caption */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2 block">
              caption
            </label>
            <MentionInput
              multiline
              rows={4}
              value={caption}
              onChange={setCaption}
              testId="caption-input"
              placeholder="Describe your video, drop some #hashtags, tag friends with @username"
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-4 py-3 focus:outline-none focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]/40 resize-none"
            />
            <p className="font-mono text-[10px] text-white/40 mt-2">
              {caption.length}/300 · use #tags for discovery
            </p>
          </div>

          {uploading && uploadStage ? (
            <div data-testid="upload-progress-block" className="w-full space-y-2">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-white/60">
                <span data-testid="upload-progress-label">
                  {uploadStage === "processing" ? "// processing…" : "// uploading"}
                </span>
                <span data-testid="upload-progress-pct" className="text-white font-bold">
                  {uploadStage === "processing" ? "finalizing" : `${uploadProgress}%`}
                </span>
              </div>
              <div className="w-full h-3 bg-white/10 border border-white/10 overflow-hidden">
                <div
                  data-testid="upload-progress-bar"
                  className={`h-full transition-all duration-200 ${
                    uploadStage === "processing"
                      ? "bg-gradient-to-r from-[#B026FF] via-[#FF007F] to-[#B026FF] bg-[length:200%_100%] animate-[progressPulse_1.2s_ease_infinite]"
                      : mode === "photo"
                      ? "bg-[#FF007F]"
                      : "bg-[#39FF14]"
                  }`}
                  style={{
                    width: uploadStage === "processing" ? "100%" : `${Math.max(2, uploadProgress)}%`,
                    boxShadow:
                      mode === "photo"
                        ? "0 0 12px rgba(255,0,127,0.55)"
                        : "0 0 12px rgba(57,255,20,0.55)",
                  }}
                />
              </div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 text-center pt-1">
                {uploadStage === "processing"
                  ? "server is transcoding + generating stream — hang tight"
                  : "keep this tab open until it hits 100%"}
              </p>
            </div>
          ) : (
            <button
              type="submit"
              disabled={
                uploading ||
                (mode === "video"
                  ? !file
                  : mode === "link"
                  ? !embedUrl.trim() || Boolean(embedPreviewError)
                  : !photoFiles.length)
              }
              data-testid="submit-upload-btn"
              className={`w-full text-black font-bold uppercase tracking-widest py-4 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 ${
                mode === "photo"
                  ? "bg-[#FF007F] text-white hover:shadow-[0_0_25px_rgba(255,0,127,0.6)]"
                  : "bg-[#39FF14] hover:shadow-[0_0_25px_rgba(57,255,20,0.6)]"
              }`}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  broadcasting…
                </>
              ) : mode === "photo" ? (
                <>post {photoFiles.length > 1 ? `${photoFiles.length} slides` : "photo"}{audioFile ? " + track" : ""}</>
              ) : (
                <>publish to the feed</>
              )}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
